import { showConfirm } from '../components/ConfirmDialog';
import LayoutPresetsPanel from '../components/LayoutPresetsPanel';
import PageHeader from '../components/PageHeader';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { useProfileContext } from '../hooks/useProfileContext';
import {
  apiGetDaysheetLayoutGlobalMeta,
  apiGetDaysheetLayoutEffectiveMeta,
  apiSaveDaysheetLayoutGlobal,
  apiSaveDaysheetLayoutBand,
  isLayoutConflict,
  type LayoutMeta,
} from '../api';
import PdfPreview from '../components/PdfPreview';
import LayoutAccessDenied from '../components/LayoutAccessDenied';
import { layoutSignature } from '../lib/layoutSignature';
import { canEditLayoutFor, canEditGlobalLayout } from '../lib/layoutAccess';
import { reportSaveError, reportSaveOk, isShowcaseMode } from '../lib/saveStatus';
import { buildDaysheetDoc, type DaysheetInput } from '../lib/daysheet';
import {
  createDefaultDaysheetLayout,
  mergeDaysheetLayouts,
  DEFAULT_SECTION_TITLES,
  SECTION_DETAILS,
  DAYSHEET_TABLE_COLUMNS,
  type DaysheetLayout,
  type DaysheetSectionId,
  type LayoutField,
} from '../lib/daysheetLayout';

type Scope = 'global' | 'band';
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Daysheet Layout Editor — admin-only editor for the daysheet PDF layout.
 *
 * Mirrors the Rider Layout Editor (src/pages/RiderEditorPage.tsx) but simpler:
 * reorder / enable / retitle the sections, edit the fixed texts (title suffix,
 * footer/header templates) and the basic style. Persistence reuses the same
 * `pdf_layouts` table under a distinct 'daysheet:' scope prefix.
 *
 * Two scopes:
 *  - "Global template"  : shared default for every band (daysheet-layouts/global)
 *  - "This band"        : override for the active band (bands/:id/daysheet-layout)
 *
 * The working layout is kept locally and the PDF preview (right column)
 * regenerates debounced on every edit — that is the WYSIWYG. Edits are
 * autosaved (debounced) and flushed before switching band/scope so nothing is
 * lost; the Save button persists immediately.
 */

/* P3d: these maps hold i18n KEYS (not English text) — they are rendered with
   t() below. Structure and lookup keys are unchanged. */
const TEXT_FIELD_LABELS: Record<string, string> = {
  titleSuffix: 'layoutEditor.titleSuffix',
  footerLeftTemplate: 'riderEditor.footerLeft',
  footerCenterTemplate: 'riderEditor.footerCenter',
  footerRightTemplate: 'riderEditor.footerRight',
  headerLeftTemplate: 'layoutEditor.headerLeft',
  headerCenterTemplate: 'layoutEditor.headerCenter',
  headerRightTemplate: 'layoutEditor.headerRight',
};

const STYLE_NUM_LABELS: Record<string, string> = {
  titleFontSize: 'riderEditor.titleSize',
  subtitleFontSize: 'riderEditor.subtitleSize',
  headingFontSize: 'riderEditor.headingSize',
  labelFontSize: 'layoutEditor.labelSize',
  bodyFontSize: 'riderEditor.bodySize',
  footerFontSize: 'riderEditor.footerSize',
};

const STYLE_COLOR_LABELS: Record<string, string> = {
  titleColor: 'layoutEditor.titleColor',
  subtitleColor: 'layoutEditor.subtitleColor',
  headingColor: 'riderEditor.headingColor',
  labelColor: 'layoutEditor.labelColor',
  textColor: 'riderEditor.textColor',
  footerColor: 'riderEditor.footerColor',
};

/** Representative sample data so the preview shows a realistic daysheet. */
function buildPreviewInput(bandName?: string, venueName?: string): DaysheetInput {
  return {
    show: {
      id: 'preview',
      name: 'Sample Show — Summer Tour 2026',
      date: '2026-08-15T19:00:00',
      status: 'confirmed',
      event_type: 'concert',
      welcome_note: '',
      timezone: 'CET',
      electricity: '230V / 16A',
      wifi_venue: 'Free Wi-Fi backstage',
      currency_note: 'EUR',
      currency_rate: '100 CZK ≈ 4 EUR',
      luggage_notes: 'Load in at the rear stage door.',
      duration_minutes: 120,
      stage_size: '12m × 8m',
      artist_pass: '5 artist + 2 crew',
      passport_reminder: 'Bring your passport for the border check.',
      notes: 'Please keep the dressing rooms quiet before the show. Catering is served at 18:00.',
    } as any,
    roadmap: [
      { sort_order: 1, time_from: '14:00', time_to: '16:00', activity: 'Load in & setup', responsible: 'Crew' },
      { sort_order: 2, time_from: '16:30', time_to: '17:00', activity: 'Soundcheck', responsible: 'FOH' },
      { sort_order: 3, time_from: '19:00', time_to: '19:30', activity: 'Doors open', responsible: 'Venue' },
      { sort_order: 4, time_from: '20:00', time_to: '22:00', activity: 'Show', responsible: 'Band' },
    ] as any,
    guests: [
      { name: 'Anna K.', role: 'Record label', check_in_status: 'confirmed' },
      { name: 'Jonas M.', role: 'Press', check_in_status: 'pending' },
    ] as any,
    contacts: [
      { role: 'Tour Manager', name: 'Erik Hansen', phone: '+45 12 34 56 78', email: 'erik@example.com' },
      { role: 'Venue', name: 'Main Stage', phone: '+49 30 123 456', email: 'stage@example.com' },
    ] as any,
    accommodations: [
      { id: 'h1', hotel_name: 'Grand Palace Hotel', address: '1 Main Street', city: 'Berlin', phone: '+49 30 999 888', confirmation_number: 'ABC123' },
    ] as any,
    rooming: [
      { accommodation_id: 'h1', role_at_date: 'Erik Hansen', room_number: '204' },
      { accommodation_id: 'h1', role_at_date: 'Anna K.', room_number: '307' },
    ] as any,
    bandName,
    venueName: venueName || 'Main Stage',
    venue: {
      id: 'preview',
      name: venueName || 'Main Stage',
      address: '1 Main Street',
      city: 'Berlin',
      zip: '10115',
      country: 'Germany',
      phone: '+49 30 123 456',
      email: 'stage@example.com',
      website: 'www.mainstage.de',
      parking: 'Parkhaus Sihlcity / Coordinates 47.35 | 8.52',
      electricity: '230V / 16A',
      timezone: 'Europe/Berlin',
    } as any,
  };
}

export default function DaysheetLayoutEditorPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { activeProfileId, getProfile } = useProfileContext();
  const activeProfile = activeProfileId ? getProfile(activeProfileId) : null;

  // Platform admin, the read-only showcase (demo) account, a band OWNER, or a
  // member the owner granted the `layout.daysheet` capability on this band.
  // Exactly the server rule
  // (`canWriteLayout(req, bandId, 'daysheet')` in server/authz.js). (2026-09-25)
  const isSuperAdmin = user?.global_role === 'superadmin';
  const isShowcase = !!user?.is_showcase;
  const canEditLayouts = canEditLayoutFor({
    isSuperAdmin,
    isShowcase,
    bandRole: isSuperAdmin ? 'owner' : activeProfile?.my_role,
    permissions: activeProfile?.permissions,
    layoutAccess: activeProfile?.layout_access,
  }, 'daysheet');
  // The GLOBAL template is shared by every band, so `layout_access` does NOT
  // open it: admin-only, and its tab is hidden for anybody else. A non-admin
  // starts on the BAND scope (the tab it can actually save).
  const canEditGlobalTemplate = canEditGlobalLayout(isSuperAdmin);

  // Working layout (unsaved edits live here).
  const [scope, setScope] = useState<Scope>(() => (canEditGlobalTemplate ? 'global' : 'band'));
  const [layout, setLayout] = useState<DaysheetLayout>(() => createDefaultDaysheetLayout());
  const [loaded, setLoaded] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  // Drag & drop reordering of sections (HTML5 native).
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  // When the editor opens inside a band, default to the BAND scope. Only
  // auto-switch once — a manual Global/Band choice is never overridden.
  const autoScopedRef = useRef(false);
  // Canonical signature of the layout as last LOADED or SAVED — the only thing
  // that decides whether a write is needed (see src/lib/layoutSignature.ts): a
  // scope switch reloads/re-merges the layout into a new object, so object
  // identity (or a hand-set dirty flag) falsely looks like a user edit and let
  // the flush overwrite the scope being left.
  const savedSigRef = useRef<string | null>(null);
  // Concurrency token of the layout we last LOADED/SAVED
  // (`pdf_layouts.last_modified`): sent on every write so the server answers 409
  // instead of overwriting when another tab/session wrote it first (2026-09-16).
  const savedLastModifiedRef = useRef<number | null>(null);
  // Always-fresh signature of the WORKING layout (assigned on every render).
  const layoutSigRef = useRef<string>('');
  layoutSigRef.current = layoutSignature(layout);
  // Always-fresh refs so the flush effect can persist edits under the PREVIOUS
  // scope/band before switching.
  const layoutRef = useRef(layout);
  layoutRef.current = layout;
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const bandRef = useRef(activeProfileId);
  bandRef.current = activeProfileId;

  // Preview state — the docDef is rasterized to per-page images by <PdfPreview>
  // so every page renders on iOS (an iframe shows only page 1). (2026-08-13)
  const [pdfDoc, setPdfDoc] = useState<any | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const genTokenRef = useRef(0);

  const bandName = activeProfileId ? getProfile(activeProfileId)?.name : undefined;

  // Auto-default to the BAND scope when a band is active.
  useEffect(() => {
    if (autoScopedRef.current) return;
    if (activeProfileId) {
      autoScopedRef.current = true;
      setScope('band');
    }
  }, [activeProfileId]);

  // Fetch the layout of the scope being edited — data PLUS the concurrency
  // token every write must echo back.
  const fetchLayoutForScope = useCallback(async (): Promise<LayoutMeta> => {
    if (scope === 'global') return apiGetDaysheetLayoutGlobalMeta();
    if (activeProfileId) return apiGetDaysheetLayoutEffectiveMeta(activeProfileId);
    return { data: null, lastModified: null };
  }, [scope, activeProfileId]);

  // Apply a loaded layout: merged over the defaults, then baselined for the
  // dirty test (signature) AND for the concurrency token.
  const applyLoadedLayout = useCallback((meta: LayoutMeta | null) => {
    const next = meta?.data
      ? mergeDaysheetLayouts(createDefaultDaysheetLayout(), meta.data)
      : createDefaultDaysheetLayout();
    setLayout(next);
    savedSigRef.current = layoutSignature(next);
    savedLastModifiedRef.current = meta?.lastModified ?? null;
  }, []);

  // Load the saved layout for the current scope (fall back to defaults when
  // no row exists yet). Reset preview state on scope/band change.
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      if (cancelled) return;
      setLoaded(false);
      setSaveStatus('idle');
      setSaveError(null);
      setPdfDoc(null);
      setPdfError(null);
      try {
        const saved = await fetchLayoutForScope();
        if (cancelled) return;
        applyLoadedLayout(saved);
      } catch (err: any) {
        if (!cancelled) {
          setSaveError(err?.message || 'Failed to load layout');
          applyLoadedLayout(null);
        }
      } finally {
        if (!cancelled) setLoaded(true);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [scope, activeProfileId]);

  // Dirty is NOT a flag here: every write is gated on the canonical signature
  // comparison (working layout vs last loaded/saved layout) — see the autosave
  // and flush effects below.

  // ---- Editing helpers (immutable updates on the working copy) ----
  const updateSections = useCallback((fn: (sections: DaysheetLayout['sections']) => DaysheetLayout['sections']) => {
    setLayout((prev) => ({ ...prev, sections: fn(prev.sections) }));
  }, []);

  const moveSection = useCallback((index: number, dir: -1 | 1) => {
    updateSections((sections) => {
      const target = index + dir;
      if (target < 0 || target >= sections.length) return sections;
      const next = [...sections];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }, [updateSections]);

  // Drop target for drag & drop reordering: move dragged section to `dropIndex`.
  const moveSectionTo = useCallback((dragIdx: number, dropIndex: number) => {
    if (dragIdx === dropIndex) return;
    updateSections((sections) => {
      const next = [...sections];
      const [moved] = next.splice(dragIdx, 1);
      next.splice(dropIndex, 0, moved);
      return next;
    });
  }, [updateSections]);

  const toggleEnabled = useCallback((index: number) => {
    updateSections((sections) =>
      sections.map((s, i) => (i === index ? { ...s, enabled: !s.enabled } : s)),
    );
  }, [updateSections]);

  const setTitle = useCallback((index: number, title: string) => {
    updateSections((sections) =>
      sections.map((s, i) => (i === index ? { ...s, title: title || null } : s)),
    );
  }, [updateSections]);

  const togglePageBreak = useCallback((index: number) => {
    updateSections((sections) =>
      sections.map((s, i) => (i === index ? { ...s, pageBreakBefore: !s.pageBreakBefore } : s)),
    );
  }, [updateSections]);

  // Métadonnées d'une colonne de tableau (null si le champ n'est pas une colonne).
  const columnMeta = useCallback((sid: string, fid: string) => {
    const cols = DAYSHEET_TABLE_COLUMNS[sid as DaysheetSectionId] || [];
    return cols.find((c) => fid === `${sid}.${c.id}`) || null;
  }, []);

  // ---- Field-level editing (spacer / free-text / reorder) ----
  const updateField = useCallback(
    (sectionIndex: number, fieldIndex: number, patch: Partial<LayoutField>) => {
      setLayout((prev) => ({
        ...prev,
        sections: prev.sections.map((s, i) => {
          if (i !== sectionIndex) return s;
          const fields = s.fields ?? [];
          return { ...s, fields: fields.map((f, fi) => (fi === fieldIndex ? { ...f, ...patch } : f)) };
        }),
      }));
    },
    [],
  );

  const moveField = useCallback((sectionIndex: number, fieldIndex: number, dir: -1 | 1) => {
    setLayout((prev) => ({
      ...prev,
      sections: prev.sections.map((s, i) => {
        if (i !== sectionIndex) return s;
        const fields = [...(s.fields ?? [])];
        const target = fieldIndex + dir;
        if (target < 0 || target >= fields.length) return s;
        [fields[fieldIndex], fields[target]] = [fields[target], fields[fieldIndex]];
        return { ...s, fields };
      }),
    }));
  }, []);

  const toggleField = useCallback((sectionIndex: number, fieldIndex: number) => {
    setLayout((prev) => ({
      ...prev,
      sections: prev.sections.map((s, i) => {
        if (i !== sectionIndex) return s;
        const fields = s.fields ?? [];
        return { ...s, fields: fields.map((f, fi) => (fi === fieldIndex ? { ...f, enabled: !(f.enabled !== false) } : f)) };
      }),
    }));
  }, []);

  const addSpacer = useCallback((sectionIndex: number) => {
    setLayout((prev) => ({
      ...prev,
      sections: prev.sections.map((s, i) => {
        if (i !== sectionIndex) return s;
        return { ...s, fields: [...(s.fields ?? []), { id: 'spacer', enabled: true, size: 8 }] };
      }),
    }));
  }, []);

  const addText = useCallback((sectionIndex: number) => {
    setLayout((prev) => ({
      ...prev,
      sections: prev.sections.map((s, i) => {
        if (i !== sectionIndex) return s;
        return { ...s, fields: [...(s.fields ?? []), { id: 'text', enabled: true, textContent: 'Your text here' }] };
      }),
    }));
  }, []);

  const removeField = useCallback((sectionIndex: number, fieldIndex: number) => {
    setLayout((prev) => ({
      ...prev,
      sections: prev.sections.map((s, i) => {
        if (i !== sectionIndex) return s;
        const fields = [...(s.fields ?? [])];
        fields.splice(fieldIndex, 1);
        return { ...s, fields };
      }),
    }));
  }, []);

  const updateText = useCallback((key: keyof DaysheetLayout['texts'], value: string | boolean) => {
    setLayout((prev) => ({ ...prev, texts: { ...prev.texts, [key]: value } }));
  }, []);

  const updateStyleNum = useCallback((key: keyof DaysheetLayout['style'], value: number) => {
    setLayout((prev) => ({ ...prev, style: { ...prev.style, [key]: value } }));
  }, []);

  const updateStyleColor = useCallback((key: keyof DaysheetLayout['style'], value: string) => {
    setLayout((prev) => ({ ...prev, style: { ...prev.style, [key]: value } }));
  }, []);

  const resetToDefaults = useCallback(async () => {
    if (!(await showConfirm(t('layoutEditor.resetConfirm')))) return;
    setLayout(createDefaultDaysheetLayout());
    setSaveStatus('idle');
  }, []);

  // Apply a saved preset: replace the working layout (defaults merged so no
  // field is missing); the existing autosave persists it to the scope.
  const applyPreset = useCallback((data: any) => {
    setLayout(mergeDaysheetLayouts(createDefaultDaysheetLayout(), data));
    setSaveStatus('idle');
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    if (!canEditLayouts) return false;
    // Belt-and-braces: the GLOBAL template is admin-only and its tab is
    // hidden for anybody else — never write it from a stale scope value.
    if (scope === 'global' && !canEditGlobalTemplate) return false;
    setSaveStatus('saving');
    setSaveError(null);
    try {
      const token = savedLastModifiedRef.current;
      let res: any;
      if (scope === 'global') {
        res = await apiSaveDaysheetLayoutGlobal(layout, token);
      } else if (activeProfileId) {
        res = await apiSaveDaysheetLayoutBand(activeProfileId, layout, token);
      } else {
        throw new Error('No band selected');
      }
      // Baseline for the dirty test: exactly what we just persisted — plus the
      // NEW concurrency token returned by the server.
      savedSigRef.current = layoutSignature(layout);
      if (res?.lastModified != null) savedLastModifiedRef.current = res.lastModified;
      reportSaveOk('daysheet');
      setSaveStatus('saved');
      return true;
    } catch (err: any) {
      // CONFLICT (409): another tab/session wrote this layout after we loaded it.
      // Never overwrite the fresher version — reload it, re-baseline, and say so.
      if (isLayoutConflict(err)) {
        try {
          applyLoadedLayout(await fetchLayoutForScope());
        } catch {
          // Server unreachable: keep the working copy, the message still shows.
        }
        setSaveError(t('layoutEditor.conflictReloaded'));
        setSaveStatus('error');
        return false;
      }
      reportSaveError('daysheet', err);
      setSaveError(err?.message || 'Save failed');
      setSaveStatus('error');
      return false;
    }
  }, [canEditLayouts, scope, activeProfileId, layout, applyLoadedLayout, fetchLayoutForScope]);

  // ---- Autosave (debounced): persist pending edits 800ms after they settle —
  // but ONLY when the working layout really differs from the last loaded/saved
  // one (canonical signature, not object identity).
  useEffect(() => {
    if (!loaded || !canEditLayouts) return;
    if (isShowcaseMode()) return; // demo account: read-only, never autosave
    if (savedSigRef.current === null || savedSigRef.current === layoutSigRef.current) return;
    const timer = setTimeout(async () => {
      await save();
    }, 800);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layout, loaded, canEditLayouts]);

  // ---- FLUSH pending edits when switching scope/band (and on unmount): the
  // cleanup captures THIS render's scope/band, so it persists the edits under
  // the scope they were made in, before the new scope loads over them.
  useEffect(() => {
    const scopeAtRender = scope;
    const bandAtRender = activeProfileId;
    // `loaded` of THIS render: the initial mount is dirty by construction
    // (savedSigRef starts null) and the editor auto-switches to the band
    // scope on mount, so the cleanup fired BEFORE the first load resolved and
    // wrote the DEFAULT layout over the scope being left — i.e. simply OPENING
    // this editor from a band URL re-wrote the GLOBAL template (same defect
    // fixed on the Rider editor in v0.556; QA v0.559 caught it here: opening the
    // daysheet/tourbook editor bumped `last_modified` of daysheet:global /
    // tourbook:global). Nothing can have been edited before the first load
    // finishes, so skip the flush until then.
    const loadedAtRender = loaded;
    return () => {
      // Showcase (demo) account: never flush on scope/band switch — nothing is
      // ever persisted for the demo. (2026-09-15)
      if (isShowcaseMode()) return;
      if (!loadedAtRender) return;
      // Flush ONLY a layout the user actually changed (canonical signature):
      // switching scope reloads the layout into a new object, which is NOT an
      // edit and must never write — that is what used to clobber the GLOBAL
      // template with a default/band layout.
      if (savedSigRef.current === null || savedSigRef.current === layoutSigRef.current) return;
      try {
        if (scopeAtRender === 'global') {
          void apiSaveDaysheetLayoutGlobal(layoutRef.current, savedLastModifiedRef.current);
        } else if (bandAtRender) {
          void apiSaveDaysheetLayoutBand(bandAtRender, layoutRef.current, savedLastModifiedRef.current);
        }
        savedSigRef.current = layoutSigRef.current;
      } catch {
        // Best-effort flush; the autosave effect retries the current scope.
      }
    };
  }, [scope, activeProfileId, loaded]);

  // ---- Live PDF preview (debounced WYSIWYG on the WORKING layout) ----
  // Preview sample band name: printing the literal "Global Template" as the band
  // name made every {bandName} token (title, header, footer) read as a bug. Use
  // the current band's name when one is loaded, else a neutral sample.
  const previewBandName = bandName || t('layoutEditor.sampleBandName');
  const previewInput = useCallback(
    () => buildPreviewInput(previewBandName, undefined),
    [previewBandName],
  );

  useEffect(() => {
    if (!loaded || !canEditLayouts) return;
    let cancelled = false;
    const token = ++genTokenRef.current;
    const timer = setTimeout(async () => {
      try {
        setPdfError(null);
        const docDef = buildDaysheetDoc(previewInput(), layout);
        if (cancelled || token !== genTokenRef.current) return;
        setPdfDoc(docDef as any);
      } catch (err: any) {
        if (cancelled || token !== genTokenRef.current) return;
        setPdfError(err?.message || 'Preview failed');
        setPdfDoc(null);
      }
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, canEditLayouts, scope, bandName, layout]);

  if (!canEditLayouts) {
    return <LayoutAccessDenied titleKey="layoutEditor.daysheetTitle" />;
  }

  return (
    <div className="page" data-layout-editor="daysheet">
      <PageHeader
        title={t('layoutEditor.daysheetTitle')}
        subtitle={t('layoutEditor.daysheetSubtitle')}
      />

      <div className="rider-editor-scope">
        <span className="nav-section-label">{t('layoutEditor.scope')}</span>
        <div className="toggle-group">
          {canEditGlobalTemplate && (
            <button
              type="button"
              className={`btn ${scope === 'global' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setScope('global')}
            >
              {t('layoutEditor.globalTemplate')}
            </button>
          )}
          <button
            type="button"
            className={`btn ${scope === 'band' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setScope('band')}
          >
            {t('layoutEditor.thisBand')}
          </button>
        </div>
        {scope === 'band' && !activeProfileId && (
          <p className="empty-hint">{t('layoutEditor.selectBandFirst')}</p>
        )}
      </div>

      <LayoutPresetsPanel
        kind="daysheet"
        scope={scope}
        bandId={activeProfileId}
        layout={layout}
        onApply={applyPreset}
      />

      <div className="rider-editor-grid">
        <div className="rider-editor-panel">
          <div className="form-section">
            <h2>{t('layoutEditor.style')}</h2>
            <div className="form-row">
              <div className="form-group">
                <label className="form-field-label">{t(STYLE_COLOR_LABELS.titleColor)}</label>
                <input type="color" className="form-input" style={{ padding: 0, height: 38 }} value={layout.style.titleColor} onChange={(e) => updateStyleColor('titleColor', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-field-label">{t(STYLE_NUM_LABELS.titleFontSize)}</label>
                <input type="number" step="0.5" className="form-input" value={layout.style.titleFontSize} onChange={(e) => updateStyleNum('titleFontSize', Number(e.target.value))} />
              </div>
              <div className="form-group">
                <label className="form-field-label">{t(STYLE_COLOR_LABELS.subtitleColor)}</label>
                <input type="color" className="form-input" style={{ padding: 0, height: 38 }} value={layout.style.subtitleColor} onChange={(e) => updateStyleColor('subtitleColor', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-field-label">{t(STYLE_NUM_LABELS.subtitleFontSize)}</label>
                <input type="number" step="0.5" className="form-input" value={layout.style.subtitleFontSize} onChange={(e) => updateStyleNum('subtitleFontSize', Number(e.target.value))} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-field-label">{t(STYLE_COLOR_LABELS.headingColor)}</label>
                <input type="color" className="form-input" style={{ padding: 0, height: 38 }} value={layout.style.headingColor} onChange={(e) => updateStyleColor('headingColor', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-field-label">{t(STYLE_NUM_LABELS.headingFontSize)}</label>
                <input type="number" step="0.5" className="form-input" value={layout.style.headingFontSize} onChange={(e) => updateStyleNum('headingFontSize', Number(e.target.value))} />
              </div>
              <div className="form-group">
                <label className="form-field-label">{t(STYLE_COLOR_LABELS.labelColor)}</label>
                <input type="color" className="form-input" style={{ padding: 0, height: 38 }} value={layout.style.labelColor} onChange={(e) => updateStyleColor('labelColor', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-field-label">{t(STYLE_NUM_LABELS.labelFontSize)}</label>
                <input type="number" step="0.5" className="form-input" value={layout.style.labelFontSize} onChange={(e) => updateStyleNum('labelFontSize', Number(e.target.value))} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-field-label">{t(STYLE_COLOR_LABELS.textColor)}</label>
                <input type="color" className="form-input" style={{ padding: 0, height: 38 }} value={layout.style.textColor} onChange={(e) => updateStyleColor('textColor', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-field-label">{t(STYLE_NUM_LABELS.bodyFontSize)}</label>
                <input type="number" step="0.5" className="form-input" value={layout.style.bodyFontSize} onChange={(e) => updateStyleNum('bodyFontSize', Number(e.target.value))} />
              </div>
              <div className="form-group">
                <label className="form-field-label">{t(STYLE_COLOR_LABELS.footerColor)}</label>
                <input type="color" className="form-input" style={{ padding: 0, height: 38 }} value={layout.style.footerColor} onChange={(e) => updateStyleColor('footerColor', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-field-label">{t(STYLE_NUM_LABELS.footerFontSize)}</label>
                <input type="number" step="0.5" className="form-input" value={layout.style.footerFontSize} onChange={(e) => updateStyleNum('footerFontSize', Number(e.target.value))} />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h2>{t('layoutEditor.sections')}</h2>
            <p style={{ color: 'var(--text-dim)', margin: '0 0 0.5rem', fontSize: '0.85rem' }}>
              {t('layoutEditor.sectionsHint')}
            </p>
            {layout.sections.map((sec, i) => (
              <div
                key={sec.id}
                data-section-id={sec.id}
                className={`section-card ${dragIndex === i ? 'section-card-dragging' : ''}`}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); if (dragIndex !== null) moveSectionTo(dragIndex, i); setDragIndex(null); }}
                onDragEnd={() => setDragIndex(null)}
              >
                <div className="section-row">
                  <div className="section-row-reorder">
                    <span
                      className="field-drag-handle"
                      draggable
                      onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setDragIndex(i); }}
                      title={t('riderEditor.dragToReorder')}
                    >⋮⋮</span>
                    <button type="button" className="btn btn-small" onClick={() => moveSection(i, -1)} disabled={i === 0} title={t('riderEditor.moveUp')}>↑</button>
                    <button type="button" className="btn btn-small" onClick={() => moveSection(i, 1)} disabled={i === layout.sections.length - 1} title={t('riderEditor.moveDown')}>↓</button>
                  </div>
                  <label className="section-row-check">
                    <input type="checkbox" checked={sec.enabled} onChange={() => toggleEnabled(i)} />
                    <span className="text-dim">{sec.id}</span>
                  </label>
                  <input
                    className="form-input section-row-title"
                    value={sec.title ?? ''}
                    placeholder={DEFAULT_SECTION_TITLES[sec.id] || t('riderEditor.noHeading')}
                    onChange={(e) => setTitle(i, e.target.value)}
                  />
                  <label className="section-row-check" title={t('riderEditor.startNewPage')}>
                    <input type="checkbox" checked={!!(sec as any).pageBreakBefore} onChange={() => togglePageBreak(i)} />
                    <span className="text-dim">{t('layoutEditor.newPage')}</span>
                  </label>
                </div>
                <div className="section-card-body">
                  {sec.enabled && (
                    <ul className="section-detail-list">
                      {(SECTION_DETAILS[sec.id] || []).map((d) => (
                        <li key={d}>{d}</li>
                      ))}
                    </ul>
                  )}
                  {/* Field-level layout: reorder / enable / spacer / free-text */}
                  <div className="field-list" style={{ marginTop: 8 }}>
                    <div className="field-list-add" style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                      <button type="button" className="btn btn-small field-spacer-add" onClick={() => addSpacer(i)}>{t('riderEditor.addSpace')}</button>
                      <button type="button" className="btn btn-small field-text-add" onClick={() => addText(i)}>{t('riderEditor.addText')}</button>
                    </div>
                    {(sec.fields ?? []).map((f, fi) => {
                      const isSpacer = f.id === 'spacer' || f.id.endsWith('.spacer');
                      const isText = f.id === 'text' || f.id.endsWith('.text');
                      return (
                        <div key={f.id + '-' + fi} className="field-row" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4, fontSize: '0.85rem' }}>
                          <span style={{ display: 'inline-flex', gap: 2 }}>
                            <button type="button" className="btn btn-small" onClick={() => moveField(i, fi, -1)} disabled={fi === 0} title={t('riderEditor.moveUp')}>↑</button>
                            <button type="button" className="btn btn-small" onClick={() => moveField(i, fi, 1)} disabled={fi === (sec.fields ?? []).length - 1} title={t('riderEditor.moveDown')}>↓</button>
                          </span>
                          <input type="checkbox" checked={f.enabled !== false} onChange={() => toggleField(i, fi)} title={t('layoutEditor.toggleEnable')} />
                          {isSpacer ? (
                            <>
                              <span className="text-dim">{t('layoutEditor.spacer')}</span>
                              <input type="number" min={0} max={60} value={f.size ?? 8} style={{ width: 56 }} onChange={(e) => updateField(i, fi, { size: Number(e.target.value) })} />
                              <span className="text-dim">px</span>
                            </>
                          ) : isText ? (
                            <>
                              <input className="form-input" value={f.textContent ?? ''} placeholder={t('layoutEditor.freeTextPh')} onChange={(e) => updateField(i, fi, { textContent: e.target.value })} />
                            </>
                          ) : columnMeta(sec.id, f.id) ? (
                            /* Colonne de tableau (modèle rider) : libellé + largeur éditables */
                            <>
                              <input
                                className="form-input"
                                style={{ maxWidth: 200 }}
                                value={f.label ?? ''}
                                placeholder={t(columnMeta(sec.id, f.id)!.labelKey)}
                                onChange={(e) => updateField(i, fi, { label: e.target.value || null })}
                                title={t('layoutEditor.columnLabel')}
                              />
                              <input
                                type="number"
                                min={1}
                                style={{ width: 64 }}
                                data-colwidth={columnMeta(sec.id, f.id)!.id}
                                value={f.width ?? ''}
                                placeholder={String(columnMeta(sec.id, f.id)!.weight)}
                                onChange={(e) => updateField(i, fi, { width: e.target.value.trim() === '' ? undefined : Number(e.target.value) })}
                                title={t('layoutEditor.columnWidths')}
                              />
                            </>
                          ) : (
                            <span className="text-dim">{f.label || f.id}</span>
                          )}
                          <button type="button" className="btn btn-small" onClick={() => removeField(i, fi)} title={t('layoutEditor.remove')}>✕</button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="form-section">
            <h2>{t('layoutEditor.texts')}</h2>
            <div className="form-group">
              <label className="form-field-label">{t(TEXT_FIELD_LABELS.titleSuffix)}</label>
              <input className="form-input" value={layout.texts.titleSuffix} placeholder={t('layoutEditor.titleSuffixPh')} onChange={(e) => updateText('titleSuffix', e.target.value)} />
              <p className="text-dim" style={{ fontSize: '0.8rem', margin: '0.25rem 0 0' }}>
                {t('layoutEditor.appendedHeaderNote')}
              </p>
            </div>
            <label className="section-row-check" style={{ margin: '0.75rem 0', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={!!layout.texts.headerEnabled} onChange={() => updateText('headerEnabled', !layout.texts.headerEnabled)} />
              <span>{t('layoutEditor.enableHeader')}</span>
            </label>
            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-field-label">{t('layoutEditor.headerLeft')}</label>
                <input className="form-input" value={layout.texts.headerLeftTemplate} onChange={(e) => updateText('headerLeftTemplate', e.target.value)} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-field-label">{t('layoutEditor.headerCenter')}</label>
                <input className="form-input" value={layout.texts.headerCenterTemplate} onChange={(e) => updateText('headerCenterTemplate', e.target.value)} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-field-label">{t('layoutEditor.headerRight')}</label>
                <input className="form-input" value={layout.texts.headerRightTemplate} onChange={(e) => updateText('headerRightTemplate', e.target.value)} />
              </div>
            </div>
            <label className="section-row-check" style={{ margin: '0.75rem 0', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={layout.texts.footerEnabled !== false} onChange={() => updateText('footerEnabled', layout.texts.footerEnabled === false)} />
              <span>{t('layoutEditor.enableFooter')}</span>
            </label>
            <div className="form-row">
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-field-label">{t(TEXT_FIELD_LABELS.footerLeftTemplate)}</label>
                <input className="form-input" value={layout.texts.footerLeftTemplate} onChange={(e) => updateText('footerLeftTemplate', e.target.value)} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-field-label">{t(TEXT_FIELD_LABELS.footerCenterTemplate)}</label>
                <input className="form-input" value={layout.texts.footerCenterTemplate} onChange={(e) => updateText('footerCenterTemplate', e.target.value)} />
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label className="form-field-label">{t(TEXT_FIELD_LABELS.footerRightTemplate)}</label>
                <input className="form-input" value={layout.texts.footerRightTemplate} onChange={(e) => updateText('footerRightTemplate', e.target.value)} />
              </div>
            </div>
            <p className="text-dim" style={{ fontSize: '0.8rem', margin: '0.25rem 0 0' }}>
              {t('layoutEditor.placeholdersLabel')} {'{bandName}'} {'{venueName}'} {'{page}'} {'{pages}'}
            </p>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={resetToDefaults}>{t('layoutEditor.resetToDefaults')}</button>
            <button type="button" className="btn btn-primary" onClick={save} disabled={saveStatus === 'saving'}>
              {saveStatus === 'saving' ? t('riderEditor.saving') : t('riderEditor.save')}
            </button>
            {saveStatus === 'saved' && <span className="save-status-ok">{t('layoutEditor.saved')}</span>}
            {saveStatus === 'error' && saveError && <span className="save-status-err">{t('riderEditor.error', { msg: saveError })}</span>}
          </div>
        </div>

        <div className="rider-editor-preview">
          <h2>
            {t('riderEditor.pdfPreview')}
            <span className="preview-scope-badge">
              {scope === 'band' ? (bandName || t('layoutEditor.thisBand')) : t('layoutEditor.globalTemplate')}
            </span>
          </h2>
          {pdfError ? (
            <div className="rider-preview-placeholder rider-preview-error">{t('riderEditor.previewError', { msg: pdfError })}</div>
          ) : pdfDoc ? (
            <PdfPreview docDef={pdfDoc} />
          ) : (
            <div className="rider-preview-placeholder">
              {scope === 'band' && !activeProfileId ? t('riderEditor.selectBandToPreview') : t('riderEditor.generatingPreview')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
